/**
 * Claude AI Pairing Engine
 * Calls the fragrance-fetch Worker which proxies to Anthropic API
 * — keeps the API key server-side only.
 */
import { supabase } from './supabase'

const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'https://fragrance-fetch.rbarbs23.workers.dev'

// The Worker's AI routes are authenticated, so the caller's Supabase access
// token identifies who to bill the monthly budget against. Without it the
// Worker answers 401 rather than spending the Anthropic key.
export async function aiAuthHeaders() {
  const { data } = await supabase.auth.getSession()
  const token = data?.session?.access_token
  if (!token) throw new Error('Sign in to use AI features')
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

export async function generatePairings(fragrance, collection, wishListCandidates = []) {
  // Send the whole catalog-bound active collection, identically every time.
  //
  // Two things used to go wrong here. The 80-item slice was thrown away by the
  // Worker's own 60-item cap, so 20 fragrances were serialized for nothing, and
  // a 377-bottle collection reached the model as an arbitrary 16% of itself
  // (W-3). And the target was filtered out of its own collection, which made
  // this payload different for every fragrance the user opened — enough to miss
  // the prompt cache on every single call (W-1).
  //
  // So: no client cap, and no target exclusion. The Worker sorts and bounds the list,
  // and tells the model not to recommend the target back. Only fields the
  // Catalog identity and the desktop layering signals travel with every
  // candidate. The Worker constrains the model to these IDs; the browser then
  // asks the database resolver to stamp the authoritative stored identity.
  const collectionSummary = [...collection, ...wishListCandidates]
    .filter(f => f.catalog_id && !f.given_away && !f.finished && !f.finished_at)
    .map(f => ({
      catalog_id: f.catalog_id,
      source_label: f.source_label || 'From your collection',
      name: f.name,
      house: f.house,
      top_notes: f.top_notes,
      mid_notes: f.mid_notes,
      base_notes: f.base_notes,
      accord_tags: f.accord_tags,
      scent_family: f.scent_family,
      layering_role: f.layering_role,
      best_layer_pair_type: f.best_layer_pair_type,
    }))

  const res = await fetch(`${WORKER_URL}/pairings`, {
    method: 'POST',
    headers: await aiAuthHeaders(),
    body: JSON.stringify({ fragrance, collection: collectionSummary }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const err = new Error(body?.error || `Pairing engine error: ${res.status}`)
    err.usageExceeded = Boolean(body?.usage_exceeded)
    throw err
  }

  return res.json()
}

export async function fetchFragranticaData(url) {
  const res = await fetch(`${WORKER_URL}/scrape`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  })
  if (!res.ok) throw new Error('Could not fetch Fragrantica data')
  return res.json()
}
